import { MapPin, Clock, Euro, Link, File, Calendar } from "lucide-react";
import { EventsActivity, ItineraryActivity } from "../models/activity.model";
import { BaseActivityCard } from "./BaseActivityCard";
import { DocumentationList } from "@/components/ui/documentation-list";
import { MediaCarousel } from "@/components/ui/MediaCarousel";
import { formatTime } from "@/utils/formatTime";

interface EventsCardProps {
  activity: EventsActivity;
  onEdit: (activity: ItineraryActivity) => void;
  onDelete: (activity: ItineraryActivity) => void;
  onDuplicate?: (activity: ItineraryActivity) => void;
  onExpand?: () => void;
  isReadOnly?: boolean;
  onDragStart?: (activity: ItineraryActivity) => void;
  onDropBefore?: (sourceActivityId: string, targetActivityId?: string) => void;
  onDragEnd?: () => void;
  isTraveler?: boolean;
  isAdmin?: boolean;
}

export const EventsCard = ({
  activity,
  onEdit,
  onDelete,
  onDuplicate,
  onExpand,
  isReadOnly = false,
  onDragStart,
  onDropBefore,
  onDragEnd,
  isTraveler = false,
  isAdmin = false
}: EventsCardProps) => {

  const formatDateRange = (start_date: string, end_date: string) => {
    const start = new Date(start_date);
    const end = new Date(end_date);

    const startMonth = start.toLocaleDateString('en-US', { month: 'short' });
    const startDay = start.getDate();
    const endDay = end.getDate();

    return `${startMonth} ${startDay} - ${endDay}`;
  };

  const renderAlwaysVisible = () => (
    <>
      {activity.address && (
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          <span>{activity.address}</span>
        </div>
      )}
      {activity.start_date && activity.end_date && (
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4" />
          <span>{formatDateRange(activity.start_date, activity.end_date)}</span>
        </div>
      )}
      {activity.start_time && activity.end_time && (
        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span>{formatTime(activity.start_time)} - {formatTime(activity.end_time)}</span>
        </div>
      )}
      {activity.cost > 0 && activity.cost !== null && activity.cost !== undefined && (isAdmin || !isTraveler) && (
        <div className="flex items-center gap-2">
          <Euro className="h-4 w-4" />
          <span>{activity.cost}</span>
        </div>
      )}
    </>
  );

  const renderCollapsible = () => (
    <>
      {/* Note sempre visibile */}
      {activity.description && (
        <div className="mb-4">
          <p className="text-sm text-gray-600 leading-relaxed">{activity.description}</p>
        </div>
      )}

      {/* Media Gallery */}
      <MediaCarousel media={activity.media || []} />

      {/* Documentation */}
      <DocumentationList documentation={activity.documentation || []} />

      {/* Link */}
      {activity.link && (isAdmin || !isTraveler) && (
        <a
          href={activity.link}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 rounded-full border border-gray-200 bg-gray-50 mt-2 px-3 py-1 text-xs font-medium text-gray-700 hover:bg-gray-100 transition-colors"
        >
          <Link className="h-3 w-3 flex-shrink-0" />
          <span className="truncate max-w-[200px]">{activity.link}</span>
        </a>
      )}
    </>
  );

  return (
    <BaseActivityCard
      activity={activity}
      onEdit={onEdit}
      onDelete={onDelete}
      onDuplicate={onDuplicate}
      onDragStart={onDragStart}
      onDropBefore={onDropBefore}
      onDragEnd={onDragEnd}
      onExpand={onExpand}
      isReadOnly={isReadOnly}
      isTraveler={isTraveler}
      isAdmin={isAdmin}
    >
      {{
        alwaysVisible: renderAlwaysVisible(),
        collapsible: renderCollapsible()
      }}
    </BaseActivityCard>
  );
};
